import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import {
  ApiClientError,
  type OperatorWorkspaceRequest,
  type WorkspaceRequestState,
} from "@/lib/types";
import { WORKSPACE_NOTES_MAX, WORKSPACE_PAGE_LIMIT } from "@/lib/workspaceRequest";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, TextArea } from "@/components/Field";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { WorkspaceRequestPlanPanel } from "@/components/WorkspaceRequestPlan";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

const FILTER_STATES: Array<WorkspaceRequestState | ""> = [
  "",
  "requested",
  "reviewing",
  "declined",
  "canceled",
];

function requestRail(state: string): string {
  if (state === "declined" || state === "canceled") return "failed";
  if (state === "reviewing") return "running";
  return "queued";
}

function isFilterState(value: string | null): value is WorkspaceRequestState {
  return Boolean(value && (FILTER_STATES as string[]).includes(value));
}

export function OperatorWorkspaceRequestsPage() {
  const { m, locale, fmt } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateRaw = searchParams.get("state");
  const state = isFilterState(stateRaw) ? stateRaw : undefined;
  const pageRaw = Number(searchParams.get("page") || "1");
  const page = Number.isInteger(pageRaw) && pageRaw > 0 ? Math.min(pageRaw, 10000) : 1;

  const [items, setItems] = useState<OperatorWorkspaceRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [retry, setRetry] = useState(0);

  const [reviewTarget, setReviewTarget] = useState<OperatorWorkspaceRequest | null>(null);
  const [reviewState, setReviewState] = useState<"reviewing" | "declined">("reviewing");
  const [reviewNote, setReviewNote] = useState("");
  const [reviewFieldError, setReviewFieldError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  useEffect(() => {
    const dirty =
      (stateRaw && !state) ||
      (searchParams.get("page") != null && String(page) !== searchParams.get("page"));
    if (!dirty) return;
    const next = new URLSearchParams();
    if (state) next.set("state", state);
    if (page > 1) next.set("page", String(page));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [stateRaw, state, page, searchParams, setSearchParams]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    setLoading(true);
    api
      .operatorWorkspaceRequests({ page, limit: WORKSPACE_PAGE_LIMIT, state }, ac.signal)
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
        setTotal(res.total);
        setError(null);
        const maxPage = Math.max(1, Math.ceil(res.total / WORKSPACE_PAGE_LIMIT));
        if (res.page > maxPage) {
          const next = new URLSearchParams();
          if (state) next.set("state", state);
          if (maxPage > 1) next.set("page", String(maxPage));
          setSearchParams(next, { replace: true });
        }
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        setItems([]);
        setTotal(0);
        setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [page, state, retry, m.states.error, setSearchParams]);

  function writeParams(next: { state?: string; page?: number }) {
    const params = new URLSearchParams();
    const nextState =
      next.state === undefined ? state : next.state === "" ? undefined : next.state;
    const nextPage = next.page ?? page;
    if (nextState && isFilterState(nextState)) params.set("state", nextState);
    if (nextPage > 1) params.set("page", String(nextPage));
    setSearchParams(params, { replace: true });
  }

  function openReview(item: OperatorWorkspaceRequest, nextState: "reviewing" | "declined") {
    setReviewTarget(item);
    setReviewState(nextState);
    setReviewNote(item.review_note || "");
    setReviewFieldError(null);
    setReviewError(null);
  }

  async function onReview() {
    if (!reviewTarget) return;
    const trimmed = reviewNote.trim();
    if (trimmed.length < 1 || trimmed.length > WORKSPACE_NOTES_MAX) {
      setReviewFieldError(m.operatorWorkspace.noteRequired);
      queueMicrotask(() => focusFirstError({ "wr-review-note": "x" }, ["wr-review-note"]));
      return;
    }
    setReviewFieldError(null);
    setReviewBusy(true);
    setReviewError(null);
    try {
      await api.reviewWorkspaceRequest(reviewTarget.id, {
        version: reviewTarget.version,
        state: reviewState,
        review_note: trimmed,
      });
      setReviewTarget(null);
      setRetry((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setReviewError(m.operatorWorkspace.conflict);
        setRetry((n) => n + 1);
      } else {
        setReviewError(err instanceof Error ? err.message : m.operatorWorkspace.actionFailed);
      }
    } finally {
      setReviewBusy(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / WORKSPACE_PAGE_LIMIT));

  return (
    <AuthStatePanels loading={loading && items.length === 0} error={error && items.length === 0 ? error : null}>
      <div>
        <p className={ui.meta}>
          <Link to="/operator">← {m.operator.title}</Link>
        </p>
        <h1 className={shell.pageTitle}>{m.operatorWorkspace.title}</h1>
        <p className={shell.pageLead}>{m.operatorWorkspace.lead}</p>
        <p className={ui.meta}>{m.operatorWorkspace.bounds}</p>

        <div className={ui.panel} style={{ marginTop: "1rem", display: "grid", gap: "0.75rem" }}>
          <Field label={m.operatorWorkspace.stateFilter} htmlFor="wr-op-state">
            <select
              id="wr-op-state"
              className={ui.input}
              value={state ?? ""}
              onChange={(e) => writeParams({ state: e.target.value, page: 1 })}
            >
              <option value="">{m.operatorWorkspace.stateAll}</option>
              {FILTER_STATES.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {lifecycleLabel(m, s as string)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {error && items.length > 0 ? (
          <p className={ui.fieldError} role="alert">
            {error.message}
          </p>
        ) : null}

        {!loading && items.length === 0 ? (
          <StatePanel
            kind="empty"
            title={m.operatorWorkspace.empty}
            message={m.operatorWorkspace.emptyBody}
          />
        ) : null}

        {items.map((item) => {
          const canReview = item.state === "requested";
          const canDecline = item.state === "requested" || item.state === "reviewing";
          return (
            <div key={item.id} className={ui.row} style={{ cursor: "default" }}>
              <StatusRail state={requestRail(item.state)} />
              <div style={{ width: "100%" }}>
                <strong>
                  {item.project_name} · {lifecycleLabel(m, item.profile)}
                </strong>
                <div className={ui.meta}>
                  {item.tenant_name} · {lifecycleLabel(m, item.state)} ·{" "}
                  {fmt(m.workspace.summary, {
                    memory: item.memory_gb,
                    storage: item.storage_gb,
                    days: item.duration_days,
                  })}
                </div>
                <div className={ui.meta}>{formatWhen(item.created_at, locale)}</div>
                {item.notes ? (
                  <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
                    <strong>{m.workspace.notes}:</strong> {item.notes}
                  </p>
                ) : null}
                {item.review_note ? (
                  <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
                    <strong>{m.workspace.reviewNoteLabel}:</strong> {item.review_note}
                  </p>
                ) : null}
                <WorkspaceRequestPlanPanel requestId={item.id} source="operator" />
                <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.55rem" }}>
                  {canReview ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => openReview(item, "reviewing")}
                    >
                      {m.operatorWorkspace.markReviewing}
                    </Button>
                  ) : null}
                  {canDecline ? (
                    <Button type="button" variant="danger" onClick={() => openReview(item, "declined")}>
                      {m.operatorWorkspace.decline}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}

        {total > WORKSPACE_PAGE_LIMIT ? (
          <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => writeParams({ page: page - 1 })}
            >
              {m.workspace.prevPage}
            </Button>
            <span className={ui.meta}>
              {fmt(m.workspace.pageStatus, { page, pages: pageCount, total })}
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= pageCount}
              onClick={() => writeParams({ page: page + 1 })}
            >
              {m.workspace.nextPage}
            </Button>
          </div>
        ) : null}

        <ConfirmSheet
          open={Boolean(reviewTarget)}
          title={
            reviewState === "declined"
              ? m.operatorWorkspace.declineTitle
              : m.operatorWorkspace.reviewTitle
          }
          confirmLabel={
            reviewState === "declined"
              ? m.operatorWorkspace.declineConfirm
              : m.operatorWorkspace.reviewConfirm
          }
          danger={reviewState === "declined"}
          busy={reviewBusy}
          onClose={() => {
            if (reviewBusy) return;
            setReviewTarget(null);
            setReviewError(null);
            setReviewFieldError(null);
          }}
          onConfirm={() => void onReview()}
        >
          <p>{m.operatorWorkspace.customerVisibleNote}</p>
          <Field
            label={m.operatorWorkspace.reviewNote}
            htmlFor="wr-review-note"
            error={reviewFieldError ?? undefined}
          >
            <TextArea
              className="resize-none"
              style={{ resize: "none" }}
              value={reviewNote}
              maxLength={WORKSPACE_NOTES_MAX}
              onChange={(e) => setReviewNote(e.target.value.slice(0, WORKSPACE_NOTES_MAX))}
            />
          </Field>
          <Field label={m.operatorWorkspace.actionState} htmlFor="wr-review-state">
            <select
              id="wr-review-state"
              className={ui.input}
              value={reviewState}
              onChange={(e) => setReviewState(e.target.value as "reviewing" | "declined")}
              disabled={reviewTarget?.state === "reviewing"}
            >
              {reviewTarget?.state === "requested" ? (
                <option value="reviewing">{lifecycleLabel(m, "reviewing")}</option>
              ) : null}
              <option value="declined">{lifecycleLabel(m, "declined")}</option>
            </select>
          </Field>
          {reviewError ? (
            <p className={ui.fieldError} role="alert">
              {reviewError}
            </p>
          ) : null}
        </ConfirmSheet>
      </div>
    </AuthStatePanels>
  );
}
